/**
 * The in-doubt banner's body copy, selected by who is reading the order detail
 * page (SPEC-SOR-376 R14).
 *
 * The order detail page renders one banner for an order whose court receipt is
 * unresolved, and who is reading that page decides what the banner may say. A
 * platform operator is reading an ACCOUNT'S order and must not be addressed as
 * the filer; a customer reading their own order keeps the second-person voice
 * the banner has always carried. The audience split is the page's own
 * `isPlatformUser` (`useAuthStatus`) — the same predicate the fee-waiver basis
 * panel gates on — which the page maps to an `OrderViewerClass` at the call
 * site, so this module stays pure and its two variants are the whole of the
 * copy contract.
 *
 * The sentence is carried SPLIT around the order reference rather than whole:
 * the reference renders as a `<strong>` inside the sentence, so the copy is the
 * two fragments that surround it. The spaces separating each fragment from the
 * reference live inside the strings deliberately — a JSX expression value
 * inserts verbatim, while the whitespace between JSX elements is collapsed.
 */

/** Who is reading the order detail page: the filer, or platform staff. */
export type OrderViewerClass = 'operator' | 'customer';

/** The in-doubt banner's body sentence, split around the order reference. */
export interface InDoubtCopy {
  /** The sentence up to the order reference — trailing space included. */
  beforeReference: string;
  /** The sentence from the order reference on — leading space included. */
  afterReference: string;
}

/**
 * The in-doubt banner's body copy for `viewer`.
 *
 * The operator variant describes the ORDER in the third person: the reader is
 * staff looking at an account's order, not the filer, so it never addresses
 * them — no "Your order", no "you" anywhere in the sentence. The customer
 * variant is unchanged.
 */
export function inDoubtCopyFor(viewer: OrderViewerClass): InDoubtCopy {
  if (viewer === 'operator') {
    return {
      beforeReference: 'Order ',
      afterReference:
        " was submitted, but the court's receipt could not yet be confirmed. The submission is being reconciled with the court and this page will update automatically once confirmed.",
    };
  }
  return {
    beforeReference: 'Your order ',
    afterReference:
      " was submitted, but we could not yet confirm the court received it. We're reconciling with the court and this page will update automatically once confirmed.",
  };
}
